/**
 * Human leadership team behind NoJo Enterprise.
 *
 * Source: japancardetc.com/team — the same humans run NoJo's operations,
 * with the AI Operations team (Sora · Rin · Yuki · Hana · Aoi) supporting
 * them around the clock. Portraits saved locally under
 * `/public/team/humans/`.
 *
 * Adam Jones (Founder & CEO) carries an `isFounder: true` flag so the
 * OurTeam page can give him hero treatment + a "Read founder's message"
 * deep-link into the per-service founder pages.
 */

export const HUMAN_TEAM = [
  {
    slug: "adam-jones",
    isFounder: true,
    portrait: "/team/humans/adam-jones.png",
    nameEn: "Adam Jones",
    nameJa: "アダム・ジョーンズ",
    titleEn: "Founder & CEO",
    titleJa: "創業者・代表取締役CEO",
    leadEn:
      "Visionary and technical lead behind JapanETCcard.com (2013) and JapanTollReceipts.jp (2026).",
    leadJa:
      "JapanETCcard.com (2013年) および JapanTollReceipts.jp (2026年) の構想者であり、技術面のリードを務めています。",
    bioEn:
      "Adam founded JapanETCcard.com in 2013 to make Japan's ETC and expressway system fair, transparent, and accessible for foreign residents, military families, diplomats, and international businesses. The service has grown into a nationwide operation trusted by more than 18,000 customers (a June 2026 milestone). In 2026 he launched JapanTollReceipts.jp to remove the monthly burden of collecting toll receipts — for foreigners and Japanese drivers alike, and is now driving development of the next-generation JapanETCcard site and an enhanced JapanTollReceipts platform expected to launch later in 2026. Backed by a strong technical and AI-development background, Adam personally leads the architecture, automation, and security behind the platform.",
    bioJa:
      "アダムは2013年にJapanETCcard.comを立ち上げ、日本のETC・高速道路を、外国人居住者、米軍関係者、外交官、外資系企業にとって利用しやすいサービスにする取り組みを続けてきました。2026年6月には18,000名を超えるお客様にご利用いただく全国規模のサービスへと成長するという大きな節目を迎えました。2026年には、毎月のETC利用明細・利用証明書確認の手間を、外国人にも日本人ドライバーにも軽減するため、JapanTollReceipts.jpを立ち上げ、次世代のJapanETCcardサイトと、2026年後半に予定されているJapanTollReceiptsの新プラットフォームの開発も現在主導しています。テクノロジーとAI開発のバックグラウンドを活かし、プラットフォームのアーキテクチャ、自動化、セキュリティを主導しています。",
    achievements: [
      { titleEn: "Founded JapanETCcard.com", titleJa: "JapanETCcard.com 創業", year: "2013" },
      {
        titleEn: "18,000+ customers served across Japan (June 2026 milestone)",
        titleJa: "2026年6月、日本全国で18,000名超のお客様にサービスを提供という節目に到達",
        year: "2013–present",
      },
      {
        titleEn: "Founded JapanTollReceipts.jp + JTR Bridge API",
        titleJa: "JapanTollReceipts.jp および JTR Bridge API を立ち上げ",
        year: "2026",
      },
      {
        titleEn: "Platform overhaul: AI + automation across every workflow",
        titleJa: "プラットフォーム全面刷新 ─ すべての業務にAIと自動化を導入",
        year: "2026",
      },
      {
        titleEn: "Technical & AI development lead",
        titleJa: "技術・AI開発リード",
        year: "Ongoing",
      },
    ],
    currentRoleEn: "Technical & AI Development Lead",
    currentRoleJa: "技術・AI開発リード",
  },
  {
    slug: "ronald-jones",
    portrait: "/team/humans/ronald-jones.png",
    nameEn: "Ronald Jones",
    nameJa: "ロナルド・ジョーンズ",
    titleEn: "Sales Director",
    titleJa: "セールス・ディレクター",
    bioEn:
      "Brings 24 years of IT and leadership experience from his distinguished career as a U.S. Navy Chief. Oversees day-to-day operations, technology, and customer service.",
    bioJa:
      "米海軍チーフとしてのキャリアで培った24年のIT・リーダーシップ経験を活かし、日々のオペレーション、技術、カスタマーサービスを統括しています。",
    achievements: [
      { titleEn: "24 years U.S. Navy IT experience", titleJa: "米海軍ITキャリア24年", year: "" },
      { titleEn: "Operations & technology lead", titleJa: "業務およびテクノロジー責任者", year: "" },
      { titleEn: "Customer experience director", titleJa: "カスタマーエクスペリエンス・ディレクター", year: "" },
    ],
    currentRoleEn: "Operations & Customer Experience Lead",
    currentRoleJa: "オペレーション・カスタマーエクスペリエンスリード",
  },
  {
    slug: "genichi-hirayama",
    portrait: "/team/humans/genichi-hirayama.png",
    nameEn: "Genichi Hirayama",
    nameJa: "平山 玄一",
    titleEn: "Branch Manager",
    titleJa: "支店長",
    bioEn:
      "Drives new projects, research initiatives, and innovation. Leads regional expansion and logistics across Japan.",
    bioJa:
      "新規プロジェクト、研究、イノベーションを牽引。日本国内の地域展開と物流を統括しています。",
    achievements: [
      { titleEn: "Regional expansion lead", titleJa: "地域展開リード", year: "" },
      { titleEn: "Logistics management", titleJa: "物流マネジメント", year: "" },
      { titleEn: "New project initiatives", titleJa: "新規プロジェクト推進", year: "" },
    ],
    currentRoleEn: "Regional Expansion & Logistics Lead",
    currentRoleJa: "地域展開・物流リード",
  },
  {
    slug: "naoko-urashima",
    portrait: "/team/humans/naoko-urashima.png",
    nameEn: "Naoko Urashima",
    nameJa: "浦島 直子",
    titleEn: "Sales Associate",
    titleJa: "セールス・アソシエイト",
    bioEn:
      "The heart of customer operations. Handles client requests, ETC installations, shipping, and toll receipt processing.",
    bioJa:
      "カスタマーオペレーションの中心を担う存在。お客様からのご依頼、ETC車載器の取り付け、発送、通行明細の処理を担当しています。",
    achievements: [
      { titleEn: "Customer operations specialist", titleJa: "カスタマーオペレーション・スペシャリスト", year: "" },
      { titleEn: "ETC installations expert", titleJa: "ETC車載器取付エキスパート", year: "" },
      { titleEn: "Toll receipt processing", titleJa: "通行明細処理担当", year: "" },
    ],
    currentRoleEn: "Customer Operations & Installations Lead",
    currentRoleJa: "カスタマーオペレーション・設置リード",
  },
];

export const HUMAN_TEAM_STATS = [
  { valueEn: "13+", valueJa: "13年超", labelEn: "Years in Business", labelJa: "事業継続年数" },
  { valueEn: "10+", valueJa: "10名超", labelEn: "Team Members", labelJa: "チーム規模" },
  { valueEn: "37", valueJa: "37拠点", labelEn: "Military Installations Served", labelJa: "対応している米軍施設数" },
  { valueEn: "18,000+", valueJa: "18,000名超", labelEn: "Customers Served", labelJa: "ご利用のお客様数" },
];
